/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-router components
import { Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Container from "@mui/material/Container";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftButton from "components/SoftComponents/SoftButton";

// Soft UI Dashboard PRO React example components
import BaseNavbarLink from "../BaseNavbar/BaseNavbarLink";
import BaseNavbarMobile from "../BaseNavbar/BaseNavbarMobile";

// Soft UI Dashboard PRO React base styles
import breakpoints from "assets/theme/base/breakpoints";

// BaseNavbar dropdown menus
import MenuOptions from "./MenuOptions";

// Nucleus Logo
import NucleusLogo from "assets/images/nucleus_logo.png";
import { useSoftUIController } from "hooks/context";
import { useAuth0 } from "@auth0/auth0-react";
import userAPI from "services/api/user";
import { getUserMenuItems } from "utils/setup";
import LogoutButton from "components/Buttons/LogoutButton";
import { Menu, Switch } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";

function BaseNavbar({ routes, user, transparent = false, light = false, action = false }) {

  const [controller] = useSoftUIController();
  const { deviceLayout } = controller;
  const menuItems = getUserMenuItems(user);

  userAPI.loadToken(useAuth0());

  // Set Menus
  const [menuAnchor, setMenuAnchor] = useState({
    overview: null,
    devices: null,
    clearways: null,
    crowds: null,
    translations: null,
    media: null,
    operations: null,
    campaigns: null,
  });

  const [openProfileMenu, setOpenProfileMenu] = useState(false);

  // Set Handlers
  const openMenu = (name) => (domNodeOrEvent) => {
    // Check if what we received is an event or a direct DOM node
    const anchor = domNodeOrEvent?.currentTarget
      ? domNodeOrEvent.currentTarget
      : domNodeOrEvent;

    if (!anchor) return;

    setMenuAnchor((prev) => ({
      ...prev,
      [name]: anchor,
    }));
  };

  const closeMenu = (name) => () => {
    setMenuAnchor((prev) => ({ ...prev, [name]: null }));
  };

  const [mobileNavbar, setMobileNavbar] = useState(false);
  const [mobileView, setMobileView] = useState(deviceLayout == 'mobile');

  const openMobileNavbar = ({ currentTarget }) => setMobileNavbar(currentTarget.parentNode);
  const closeMobileNavbar = () => setMobileNavbar(false);

  const handleOpenProfileMenu = (event) => setOpenProfileMenu(event.currentTarget);
  const handleCloseProfileMenu = () => setOpenProfileMenu(false);

  const queryClient = useQueryClient();

  const { mutate: updateMode } = useMutation({
    mutationFn: () => userAPI.updateSystemMode(),
    // Once the DB update is successful, invalidate the 'getUser' cache
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getUser', user.user.email] });
    },
  });

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openProfileMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openProfileMenu)}
      onClose={handleCloseProfileMenu}
      sx={{ mt: 2 }}
    >
      <SoftBox display="flex" flexDirection="column">
        <SoftTypography variant="caption" textTransform="capitalize">System Mode: {user.user.mode}</SoftTypography>
        <Switch checked={user.user.mode === "dark"} onChange={() => updateMode()} />
      </SoftBox>
    </Menu>
  );

  function menuFilter(route, menuType) {
    return route.in_menu && route.key.includes(menuType);
  }

  return (
    <Container>
      <SoftBox
        py={deviceLayout == 'mobile' ? 0.5 : 1}
        px={deviceLayout == 'mobile' ? 3 : 4}
        my={deviceLayout == 'mobile' ? 1 : 2}
        mx={deviceLayout == 'mobile' ? 1 : 2}
        // width="calc(100% - 112px)"
        width={`calc(100% - ${deviceLayout == 'mobile' ? '-16px' : '16px'} - (40px * 2))`}
        borderRadius="16px"
        shadow={transparent ? "none" : "md"}
        color={light ? "white" : "dark"}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        position="fixed"
        left={0}
        zIndex={1000}
        sx={({ palette: { transparent: transparentColor, white }, functions: { rgba } }) => ({
          backgroundColor: transparent ? transparentColor.main : rgba(white.main, 1),
          backdropFilter: transparent ? "none" : `saturate(200%) blur(30px)`,
        })}
      >
        <SoftBox component={Link} to="/" sx={{ maxWidth: "120px" }}>
          <SoftBox
            component="img"
            src={NucleusLogo}
            width="100%"
            borderRadius="lg"
          />
        </SoftBox>
        <SoftBox color="inherit" display={{ xxs: "none", xl: "flex" }} m={0} p={0}>
          {menuItems.map((menuItem, itemIndex) => (
            <BaseNavbarLink
              key={itemIndex}
              name={menuItem}
              anchorEl={menuAnchor[menuItem]}
              routes={routes.filter(r => menuFilter(r, menuItem))}
              openHandler={openMenu(menuItem)}
              closeHandler={closeMenu(menuItem)}
              light={light}
            />
          ))}
        </SoftBox>
        {action &&
          (action.type === "internal" ? (
            <SoftBox display={{ xxs: "none", xl: "inline-block" }}>
              <SoftButton
                onClick={handleOpenProfileMenu}
                variant="gradient"
                color={action.color ? action.color : "dark"}
                size="small"
                circular
              >
                {action.label}
              </SoftButton>
              {renderMenu()}
            </SoftBox>
          ) : (
            <SoftBox display={{ xxs: "none", xl: "inline-block" }}>
              <SoftButton
                component={Link}
                to={"/settings"}
                variant="gradient"
                color={action.color ? action.color : "dark"}
                size="small"
                circular
              >
                {action.label}
              </SoftButton>
            </SoftBox>
          ))}
        <SoftBox
          display={{ xxs: "flex", xl: "none" }}
          lineHeight={0}
          py={1}
          gap={2}
          color="inherit"
        >
          <SoftBox
            sx={{ width: '100%', cursor: "pointer" }}
            onClick={openMobileNavbar}
          >
            <Icon fontSize="default">{mobileNavbar ? "close" : "menu"}</Icon>
          </SoftBox>
          <SoftBox
            sx={{ cursor: "pointer" }}
            component={Link}
            to={"/settings"}
          >
            <Icon fontSize="default">settings</Icon>
          </SoftBox>
        </SoftBox>
      </SoftBox>

      {menuItems.map((menuItem, itemIndex) => (
        <MenuOptions
          key={itemIndex}
          routes={routes.filter(r => menuFilter(r, menuItem))}
          route_path={menuItem}
          open={menuAnchor[menuItem]}
          close={closeMenu(menuItem)}
          openHandler={openMenu(menuItem)}
          noCollapse={routes.filter(r => r.key.includes(menuItem) && !r.collapse).length > 0}
        />
      ))}
      {mobileView && (
        <BaseNavbarMobile routes={routes} menuItems={menuItems} open={mobileNavbar} close={closeMobileNavbar} />
      )}
    </Container>
  );
}


// Typechecking props for the BaseNavbar
BaseNavbar.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  user: PropTypes.object,
  transparent: PropTypes.bool,
  light: PropTypes.bool,
  action: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      type: PropTypes.oneOf(["external", "internal"]).isRequired,
      route: PropTypes.string.isRequired,
      color: PropTypes.oneOf([
        "primary",
        "secondary",
        "info",
        "success",
        "warning",
        "error",
        "dark",
        "light",
      ]),
      label: PropTypes.string.isRequired,
    }),
  ]),
};

export default BaseNavbar;
